import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  localStorage.removeItem('token');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      const permissionsResponse = await axios.get(`${API}/auth/me/permissions`, { withCredentials: true });
      setPermissions(permissionsResponse.data.pages || []);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPermissions = async () => {
    const response = await axios.get(`${API}/auth/me/permissions`, { withCredentials: true });
    setPermissions(response.data.pages || []);
  };

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (response.data.requires_otp) {
        return { success: false, requiresOtp: true, challengeId: response.data.challenge_id, email: response.data.email };
      }
      const { user: userData } = response.data;
      setToken('cookie');
      setUser(userData);
      await fetchPermissions();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const verifyLoginOtp = async (challengeId, code) => {
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { challenge_id: challengeId, code }, { withCredentials: true });
      setToken('cookie');
      setUser(response.data.user);
      await fetchPermissions();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Invalid verification code' };
    }
  };

  const register = async (email, password, name, role, phone = null) => {
    try {
      const payload = { email, password, name, role };
      if (phone) payload.phone = phone;
      
      const response = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      const { user: userData } = response.data;
      setToken('cookie');
      setUser(userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setToken(null);
      setUser(null);
      setPermissions([]);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, permissions, login, verifyLoginOtp, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
